import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  ArrowSquareOut,
  CheckCircle,
  Clock,
  CreditCard,
  Lightning,
  Warning,
  XCircle,
} from "@phosphor-icons/react";

const STEPS = [
  {
    n: 1,
    key: "issuing_capability",
    title: "Apply for Stripe Issuing access",
    minutes: "5 min apply · 1–10 business days approval",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Stripe Issuing is gated — you can't self-serve it from your dashboard.
          Submit the Issuing contact form, share your business model
          ("US-based prepaid virtual debit cards for cross-border money
          transfer, est. volume X cards / month") + KYB docs Stripe asks
          for. Approval typically lands in <b>1–10 business days</b>.
        </p>
        <a
          href="https://stripe.com/issuing/contact"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Stripe → Issuing contact <ArrowSquareOut size={14} />
        </a>
        <div className="mt-3 rounded-xl border border-[#184A30]/30 bg-[#184A30]/5 p-3 text-sm">
          <p className="text-[#184A30] font-medium mb-1">💡 Skip the blank form anxiety</p>
          <p className="text-[#4A5C50] text-xs leading-relaxed">
            We auto-generate the entire application packet from your live
            database — TPV, sender count, OFAC posture, compliance program
            version. Copy each section into Stripe's form, or print as PDF
            to attach to your underwriting call.
          </p>
          <Link
            to="/admin/stripe-issuing-application"
            className="inline-flex items-center gap-1 text-[#184A30] text-sm font-semibold mt-2"
            data-testid="link-to-application-packet"
          >
            Open the auto-filled application packet → 
          </Link>
          <div className="mt-3 pt-3 border-t border-[#184A30]/20">
            <p className="text-[#4A5C50] text-xs leading-relaxed">
              ⚡ <b>While you wait for Stripe approval</b>, you can already mint
              <b> real Stripe Issuing Visa cards in test mode</b> (no approval
              needed) for investor demos / partner pitches / dev validation.
            </p>
            <Link
              to="/admin/stripe-issuing-demo"
              className="inline-flex items-center gap-1 text-[#D96C4A] text-sm font-semibold mt-1.5"
              data-testid="link-to-demo-mint"
            >
              Mint a test Visa card now →
            </Link>
          </div>
        </div>
        <p className="text-xs text-[#4A5C50] mt-3">
          When approved, the readiness tile above will flip{" "}
          <span className="mono">capability_card_issuing</span> from{" "}
          <span className="mono text-[#B04E2F]">inactive</span> →{" "}
          <span className="mono text-[#184A30]">active</span>.
        </p>
      </>
    ),
  },
  {
    n: 2,
    key: "issuing_balance",
    title: "Top up the Issuing balance",
    minutes: "1 day for ACH",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Once Issuing is active, Stripe creates a separate <b>Issuing
          balance</b> distinct from your Payments balance. Fund it via{" "}
          <b>Payments → Issuing → Add funds</b> using ACH from your business
          bank. This is the pool every Issuing card draws from. Stripe will
          decline any authorization if this balance hits zero, regardless
          of your internal MongoDB ledger.
        </p>
        <a
          href="https://dashboard.stripe.com/issuing/balance"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Issuing balance <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
  {
    n: 3,
    key: "webhook",
    title: "Register webhook for issuing.* events",
    minutes: "5 min",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Create a webhook endpoint pointed at{" "}
          <span className="mono">https://www.zcardon.com/api/webhook/stripe-issuing</span>{" "}
          listening to these events:
        </p>
        <ul className="mt-2 space-y-0.5 text-sm text-[#4A5C50] list-disc list-inside">
          <li><span className="mono">issuing_authorization.request</span> ← real-time approve/decline (CRITICAL — Stripe's 2s SLA, already handled server-side)</li>
          <li><span className="mono">issuing_authorization.created</span></li>
          <li><span className="mono">issuing_authorization.updated</span></li>
          <li><span className="mono">issuing_transaction.created</span> ← settlement reconciliation (already wired)</li>
          <li><span className="mono">issuing_card.updated</span></li>
        </ul>
        <p className="text-[#4A5C50] mt-3">
          Copy the <b>signing secret</b> (<span className="mono">whsec_…</span>)
          and add it to your Emergent Deploy Panel as{" "}
          <span className="mono">STRIPE_ISSUING_WEBHOOK_SECRET</span>.
          Without this set, the webhook accepts unsigned events (fine for
          sandbox but NEVER for production).
        </p>
        <a
          href="https://dashboard.stripe.com/webhooks/create"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Create webhook in Stripe <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
  {
    n: 4,
    key: "backend_wiring",
    title: "Backend wiring (already implemented)",
    minutes: "DONE ✓",
    body: (
      <>
        <div className="rounded-xl border border-[#184A30]/30 bg-[#184A30]/5 p-3 text-sm text-[#184A30] mb-3">
          <b>Good news:</b> all the backend code is already shipped. The day
          Stripe approves your Issuing application + you set{" "}
          <span className="mono">ISSUANCE_PROVIDER=stripe</span> in your env,
          newly issued cards become real Visa virtual cards. No more code from me.
        </div>
        <p className="text-[#4A5C50] leading-relaxed">Already wired:</p>
        <ul className="mt-2 space-y-1 text-sm text-[#4A5C50] list-disc list-inside">
          <li><span className="mono">StripeIssuingProvider</span> adapter — creates real cardholders + cards with PAN/CVC expansion</li>
          <li><span className="mono">POST /api/webhook/stripe-issuing</span> — synchronous authorization.request handler enforcing per-card balance in &lt;2 s (signed via <span className="mono">STRIPE_ISSUING_WEBHOOK_SECRET</span>)</li>
          <li><span className="mono">individual.card_issuing.user_terms_acceptance</span> auto-attached to every Cardholder (Stripe live mode requirement)</li>
          <li>Per-customer override (<span className="mono">PUT /api/admin/users/&#123;id&#125;/issuance-provider</span>) — pilot real Visa for a single customer before global flip</li>
          <li>Admin smoke-test button (Admin → Issuance provider tile) — verifies the live Stripe key reaches Issuing API in one click</li>
          <li>Card migration: <span className="mono">POST /api/admin/cards/&#123;id&#125;/migrate-to-network</span> bulk-converts existing wallet cards to real Visa</li>
        </ul>
      </>
    ),
  },
  {
    n: 5,
    key: "address_env_vars",
    title: "Set business-address env vars (live mode)",
    minutes: "2 min",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          In test mode, Stripe accepts the SF placeholder address baked into
          the code. In <b>live mode</b>, Stripe ties each cardholder to your
          real US business address (KYB). Set these env vars on the deploy
          panel so every cardholder created carries your real corporate info:
        </p>
        <ul className="mt-2 space-y-0.5 text-sm text-[#4A5C50] list-disc list-inside">
          <li><span className="mono">STRIPE_ISSUING_DEFAULT_LINE1</span> — street address (e.g. "1 Hacker Way")</li>
          <li><span className="mono">STRIPE_ISSUING_DEFAULT_CITY</span></li>
          <li><span className="mono">STRIPE_ISSUING_DEFAULT_STATE</span> — 2-letter US state code</li>
          <li><span className="mono">STRIPE_ISSUING_DEFAULT_POSTAL</span></li>
          <li><span className="mono">STRIPE_ISSUING_DEFAULT_COUNTRY</span> — defaults to <span className="mono">US</span></li>
          <li><span className="mono">STRIPE_ISSUING_TERMS_IP</span> — server egress IP (placeholder OK)</li>
        </ul>
        <p className="text-[#4A5C50] mt-3 leading-relaxed text-xs">
          Once Stripe approves Apple/Google push-provisioning for your account,
          we'll capture each cardholder's <b>own</b> IP during the wallet
          add-to-phone flow — these env vars are just the issuance-time
          fallback so Stripe never rejects for missing fields.
        </p>
      </>
    ),
  },
  {
    n: 6,
    key: "flip_the_switch",
    title: "Flip the switch",
    minutes: "30 sec",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          When steps 1–5 are green, add one final env var on the Deploy Panel:
        </p>
        <pre className="mt-2 bg-[#112318] text-[#FBF9F6] rounded-xl p-3 text-xs mono overflow-x-auto">
          ISSUANCE_PROVIDER=stripe
        </pre>
        <p className="text-[#4A5C50] leading-relaxed mt-3">
          Redeploy. From that moment on, every new card minted via{" "}
          <span className="mono">/api/cards/issue</span> is a real Visa
          virtual debit card. Existing closed-loop cards keep working —
          they're not migrated unless you explicitly call the bulk-migrate
          endpoint per-customer.
        </p>
        <p className="text-[#4A5C50] text-xs italic mt-3">
          Want to pilot first? Use the Admin → Users tab to set a
          per-customer override (<span className="mono">issuance_provider_override = stripe</span>)
          on one or two volunteer customers before flipping globally.
        </p>
      </>
    ),
  },
  {
    n: 7,
    key: "wallets",
    title: "Apple Wallet / Google Pay push provisioning",
    minutes: "3–8 weeks + native app",
    body: (
      <>
        <div className="rounded-xl border border-[#CBA35C]/40 bg-[#CBA35C]/5 p-3 text-sm text-[#8C6B21] mb-2">
          <b>Honest warning:</b> push provisioning is the hardest piece
          and is <b>only available in native iOS / Android apps</b> — not
          on the web. Stripe says it explicitly. To ship "Add to Apple
          Wallet" you need:
        </div>
        <ul className="mt-2 space-y-1 text-sm text-[#4A5C50] list-disc list-inside">
          <li>An iOS app (and an Android app for Google Pay)</li>
          <li>Apple entitlement <span className="mono">com.apple.developer.payment-pass-provisioning</span> — requested via Stripe Issuing support, granted by Apple</li>
          <li>Google issuer onboarding + TapAndPay allowlist</li>
          <li>The Stripe iOS/Android Issuing SDKs integrated with an EphemeralKeyProvider that calls our backend</li>
        </ul>
        <p className="text-[#4A5C50] mt-3 leading-relaxed">
          For the immediate web launch, cardholders can still <b>manually
          add the card</b> to Apple Wallet by entering the PAN/CVC into
          the Wallet app — Stripe Issuing Elements (built in step 4)
          shows them the credentials securely. Push provisioning is
          a polish layer, not a blocker.
        </p>
      </>
    ),
  },
];

const StatusDot = ({ ok, warn, label }) => {
  if (ok) return <span className="inline-flex items-center gap-1 text-[#184A30] text-xs"><CheckCircle size={14} weight="fill" /> {label}</span>;
  if (warn) return <span className="inline-flex items-center gap-1 text-[#8C6B21] text-xs"><Clock size={14} weight="fill" /> {label}</span>;
  return <span className="inline-flex items-center gap-1 text-[#B04E2F] text-xs"><XCircle size={14} weight="fill" /> {label}</span>;
};

export default function StripeIssuingGuide() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/issuing/status"); setStatus(r.data); }
    catch { setStatus({ error: "Probe failed" }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const flags = {
    issuing_capability: status?.capability_card_issuing === "active",
    issuing_capability_pending: ["pending", "in_review"].includes(status?.capability_card_issuing),
    issuing_balance: (status?.issuing_balance_usd ?? 0) > 0,
    webhook: !!status?.webhook_secret_configured,
  };
  const stepsDone = [flags.issuing_capability, flags.issuing_balance, flags.webhook].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318]" data-testid="stripe-issuing-guide">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/admin/setup" className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]" data-testid="issuing-back">
          <ArrowLeft size={14} /> Back to Setup Checklist
        </Link>
        <header className="mt-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Real tap-to-pay · the real path</p>
          <h1 className="serif text-4xl mt-1">Stripe Issuing rollout</h1>
          <p className="text-[#4A5C50] mt-3 leading-relaxed max-w-2xl">
            Switch from closed-loop QR cards to <b>real Visa virtual debit cards</b> that work at every Visa terminal worldwide.
            Total wall-clock time: <b>~2-4 weeks</b> (mostly Stripe sales approval + ACH funding clearing). Total
            code time on my side: <b>2-3 days</b> once steps 1-3 are green. No production cardholder gets minted until
            every readiness check below is green.
          </p>
        </header>

        {/* Live readiness probe */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="issuing-readiness-tile">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Live readiness</p>
              <h2 className="serif text-2xl mt-0.5">{stepsDone} of 3 prerequisites green</h2>
            </div>
            <Button size="sm" variant="outline" onClick={refresh} disabled={loading} className="rounded-xl text-xs border-[#184A30] text-[#184A30]" data-testid="issuing-refresh">
              {loading ? "Checking…" : "Refresh"}
            </Button>
          </div>
          {status?.error && (
            <p className="mt-3 text-xs text-[#B04E2F] flex items-center gap-1"><Warning size={12} weight="fill" />{status.error}</p>
          )}
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="issuing-flag-capability">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">Issuing capability</p>
              <p className="mt-1"><StatusDot ok={flags.issuing_capability} warn={flags.issuing_capability_pending} label={status?.capability_card_issuing || "—"} /></p>
            </div>
            <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="issuing-flag-balance">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">Issuing balance</p>
              <p className="mt-1"><StatusDot ok={flags.issuing_balance} label={status?.issuing_balance_usd != null ? `$${status.issuing_balance_usd.toLocaleString(undefined,{minimumFractionDigits:2})}` : "$0.00"} /></p>
            </div>
            <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="issuing-flag-webhook">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">Webhook configured</p>
              <p className="mt-1"><StatusDot ok={flags.webhook} label={flags.webhook ? "ready" : "not set"} /></p>
            </div>
          </div>
          {status && (
            <div className="mt-3 text-[11px] text-[#4A5C50] flex flex-wrap gap-3">
              <span>Account: {status.account_country || "?"} · {status.stripe_mode || "?"} mode</span>
              {status.cardholders_count != null && <span>Cardholders in Stripe: {status.cardholders_count}</span>}
              {status.cards_count != null && <span>Cards in Stripe: {status.cards_count}</span>}
            </div>
          )}
        </section>

        {/* Steps */}
        <ol className="mt-8 space-y-5" data-testid="issuing-steps">
          {STEPS.map((s) => {
            const done = (s.key === "issuing_capability" && flags.issuing_capability)
              || (s.key === "issuing_balance" && flags.issuing_balance)
              || (s.key === "webhook" && flags.webhook);
            return (
              <li key={s.n} className={`rounded-2xl border ${done ? "border-[#184A30]/40 bg-[#184A30]/5" : "border-[#E5E0D8] bg-white"} p-5`} data-testid={`issuing-step-${s.n}`}>
                <div className="flex items-start gap-4">
                  <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm serif ${done ? "bg-[#184A30] text-white" : "bg-[#FBF9F6] border border-[#E5E0D8] text-[#112318]"}`}>
                    {done ? <CheckCircle size={18} weight="fill" /> : s.n}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="serif text-xl">{s.title}</h2>
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50] inline-flex items-center gap-1"><Clock size={11} />{s.minutes}</span>
                    </div>
                    <div className="mt-2 text-sm">{s.body}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        {/* CTA */}
        {flags.issuing_capability && flags.issuing_balance && flags.webhook && (
          <section className="mt-8 rounded-2xl border border-[#184A30]/40 bg-[#184A30]/5 p-5" data-testid="issuing-ready-cta">
            <div className="flex items-start gap-3">
              <Lightning size={22} weight="fill" className="text-[#184A30]" />
              <div className="flex-1">
                <p className="serif text-xl text-[#184A30]">All prerequisites green ✓</p>
                <p className="text-sm text-[#184A30] mt-1 leading-relaxed">
                  Tell the agent <span className="mono">"go wire Issuing"</span> and I'll build the cardholder + card + authorization
                  webhook code in 2-3 days. Next card you mint will be a real tappable Visa.
                </p>
              </div>
            </div>
          </section>
        )}

        <div className="mt-8 text-center">
          <Link to="/admin/setup" className="text-sm text-[#184A30] hover:underline">← Back to Setup Checklist</Link>
        </div>
      </div>
    </div>
  );
}
